import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

export interface AdvancedSearchFilters {
  searchQuery: string;
  categoryFilter: string;
  priceMin: string;
  priceMax: string;
  statusFilter: 'all' | 'active' | 'inactive';
  savingsMin: string;
  savingsMax: string;
}

interface AdvancedProductSearchProps {
  filters: AdvancedSearchFilters;
  onFiltersChange: (filters: AdvancedSearchFilters) => void;
  categories: string[];
  totalResults: number;
}

export function AdvancedProductSearch({ 
  filters, 
  onFiltersChange, 
  categories, 
  totalResults 
}: AdvancedProductSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = (key: keyof AdvancedSearchFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      searchQuery: '',
      categoryFilter: 'all',
      priceMin: '',
      priceMax: '',
      statusFilter: 'all',
      savingsMin: '',
      savingsMax: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.searchQuery !== '' ||
           filters.categoryFilter !== 'all' ||
           filters.priceMin !== '' ||
           filters.priceMax !== '' ||
           filters.statusFilter !== 'all' ||
           filters.savingsMin !== '' ||
           filters.savingsMax !== '';
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.categoryFilter !== 'all') count++;
    if (filters.priceMin || filters.priceMax) count++;
    if (filters.statusFilter !== 'all') count++;
    if (filters.savingsMin || filters.savingsMax) count++;
    return count;
  };

  return (
    <Card className="mb-6">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Advanced Search & Filters
                {hasActiveFilters() && (
                  <Badge variant="secondary" className="ml-2">
                    {getActiveFilterCount()} active
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{totalResults} results</span>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Quick Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by product name, description, or size..."
                value={filters.searchQuery}
                onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Category Filter */}
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <Select value={filters.categoryFilter} onValueChange={(value) => handleFilterChange('categoryFilter', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <Select value={filters.statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive') => handleFilterChange('statusFilter', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div>
                <Label className="text-sm font-medium">Our Price Range</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Min"
                    type="number"
                    step="0.01"
                    value={filters.priceMin}
                    onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                  />
                  <span className="flex items-center text-gray-500">-</span>
                  <Input
                    placeholder="Max"
                    type="number"
                    step="0.01"
                    value={filters.priceMax}
                    onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                  />
                </div>
              </div>

              {/* Savings Range */}
              <div>
                <Label className="text-sm font-medium">Savings % Range</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Min %"
                    type="number"
                    step="0.1"
                    value={filters.savingsMin}
                    onChange={(e) => handleFilterChange('savingsMin', e.target.value)}
                  />
                  <span className="flex items-center text-gray-500">-</span>
                  <Input
                    placeholder="Max %"
                    type="number"
                    step="0.1"
                    value={filters.savingsMax}
                    onChange={(e) => handleFilterChange('savingsMax', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                {hasActiveFilters() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear All Filters
                  </Button>
                )}
              </div>
              
              <div className="text-sm text-gray-500">
                Showing {totalResults} products
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters() && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {filters.searchQuery && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: "{filters.searchQuery}"
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => handleFilterChange('searchQuery', '')}
                    />
                  </Badge>
                )}
                {filters.categoryFilter !== 'all' && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Category: {filters.categoryFilter.replace('-', ' ')}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => handleFilterChange('categoryFilter', 'all')}
                    />
                  </Badge>
                )}
                {filters.statusFilter !== 'all' && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Status: {filters.statusFilter}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => handleFilterChange('statusFilter', 'all')}
                    />
                  </Badge>
                )}
                {(filters.priceMin || filters.priceMax) && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Price: ${filters.priceMin || '0'} - ${filters.priceMax || '∞'}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => {
                        handleFilterChange('priceMin', '');
                        handleFilterChange('priceMax', '');
                      }}
                    />
                  </Badge>
                )}
                {(filters.savingsMin || filters.savingsMax) && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Savings: {filters.savingsMin || '0'}% - {filters.savingsMax || '∞'}%
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => {
                        handleFilterChange('savingsMin', '');
                        handleFilterChange('savingsMax', '');
                      }}
                    />
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}